/**
 * Data transformation utilities for converting backend data to frontend types
 */

import {
  RefinedResponse,
  DraftedQuestion,
  QuestionMatrixRow,
  QuestionSelectionMetadata,
  EvaluationCriteria,
  Question,
  PastTenderMatch,
  FinalReport,
  FinalReportResponse,
  ComplianceItem,
  EvidenceSummary,
  AIDisclosure,
  CriterionBreakdown,
  EvaluationCriterion,
} from '../types';

/**
 * Detect whether a run was started in GENERATE mode (drafts authored from
 * scratch, no prior baseline). Two signals, in order of trustworthiness:
 *   1. analysisData.response_mode === 'generate' — explicit, set by the
 *      upload modal and streamed back as part of state values.
 *   2. Backend fingerprint — initial_reviewer_agent is skipped in GENERATE
 *      mode (response_subgraph.py:10446), so initial_review_result is null
 *      AND compare_score_improvement returns an empty improvement_analysis
 *      with the explicit "no baseline" message.
 * The fallback covers the case where response_mode hasn't streamed yet but
 * the run-shape unambiguously indicates GENERATE.
 */
export function isGenerateMode(analysisData: any): boolean {
  if (!analysisData) return false;
  if (analysisData.response_mode === 'generate') return true;
  const ia = analysisData.improvement_analysis;
  const iaIsEmptyObject =
    ia && typeof ia === 'object' && !Array.isArray(ia) && Object.keys(ia).length === 0;
  return !analysisData.initial_review_result && iaIsEmptyObject;
}

/**
 * Transform refined_questions from backend to RefinedResponse format
 * Handles both the raw refined_questions format and potential RefinedResponse format
 */
export function transformRefinedQuestions(refinedQuestions: any[] | undefined): RefinedResponse[] {
  if (!refinedQuestions || !Array.isArray(refinedQuestions)) {
    return [];
  }

  return refinedQuestions.map((item, index) => {
    // If it's already in RefinedResponse format, return as is
    if (item.question_id && item.refined_response) {
      return item as RefinedResponse;
    }

    // Backend (generic_refinement / refine_responses_with_patterns) now produces
    // grounded improvement_score, improvement_summary, and refinement_reason for
    // every refined response via _score_single_refinement. We pass those through
    // verbatim — no fabricated fallbacks. If a value is missing (because scoring
    // failed for that one item), it stays null/empty and the UI surfaces "—"
    // rather than a fake number or a generic boilerplate string.
    const rawScore = item.improvement_score;
    const score: number | null =
      typeof rawScore === 'number' ? rawScore : null;

    const transformedItem: RefinedResponse = {
      question_id: item.id || item.question_id || `refined-${index}`,
      question: item.text || item.question || item.refined_question || '',
      original_response: item.original_response || item.response || '',
      refined_response: item.refined_response || item.improved_response || item.response || '',
      improvement_summary: item.improvement_summary || '',
      improvement_score: score as unknown as number, // keep field shape; UI checks for nullish before display
      refinement_reason: item.refinement_reason || ''
    };

    return transformedItem;
  });
}

/**
 * Check if the data appears to be refined questions/responses
 */
export function hasRefinedQuestionsData(data: any): boolean {
  return data && Array.isArray(data) && data.length > 0;
}

// ────────────────────────────────────────────────────────────────────────────
// P0 — Question Matrix transformer
// Merges three upstream state fields into one unified row list:
//   • state.questions (all extracted, may have an inline response or not)
//   • state.refined_questions (those that had content; AI improved)
//   • state.drafted_questions (those that lacked content; AI authored)
// Plus joins:
//   • state.evaluation_criteria → criterion label per question (best-effort)
//   • state.question_selection_metadata → status / score breakdown for the
//     explainability drawer
// ────────────────────────────────────────────────────────────────────────────

/** Match a question to the most relevant evaluation criterion via simple
 *  token overlap (mirrors the backend's selection scorer). Returns a label
 *  like "Technology 20%" or undefined if nothing meaningful matches. */
function matchCriterion(
  questionText: string,
  criteria: EvaluationCriteria[] | undefined
): string | undefined {
  if (!criteria || !criteria.length || !questionText) return undefined;
  const qWords = new Set(
    questionText
      .toLowerCase()
      .split(/\W+/)
      .filter((w) => w.length > 3) // drop short/common tokens
  );
  let best: { name: string; weight?: number; overlap: number } | null = null;
  for (const c of criteria) {
    const name = (c as any).name || (c as any).criterion;
    const weight = (c as any).weight;
    if (!name) continue;
    const cWords = new Set(
      String(name)
        .toLowerCase()
        .split(/\W+/)
        .filter((w) => w.length > 3)
    );
    let overlap = 0;
    cWords.forEach((w) => {
      if (qWords.has(w)) overlap += 1;
    });
    // Special-case keyword bridges that token overlap misses.
    const qLower = questionText.toLowerCase();
    if (
      (/data|gdpr|ip address|consent/.test(qLower) && /data/i.test(name)) ||
      (/cost|price|pricing|budget/.test(qLower) && /pric/i.test(name)) ||
      (/design|ux|ui|accessib/.test(qLower) && /design/i.test(name)) ||
      (/games|measure|gamif|evaluat/.test(qLower) && /evaluation|gamif/i.test(name)) ||
      (/platform|browser|app|technology|architecture/.test(qLower) &&
        /technolog/i.test(name))
    ) {
      overlap += 2;
    }
    if (overlap > 0 && (!best || overlap > best.overlap)) {
      best = { name: String(name), weight, overlap };
    }
  }
  if (!best) return undefined;
  if (typeof best.weight === 'number' && best.weight > 0) {
    return `${best.name} ${best.weight}%`;
  }
  return best.name;
}

/** P7 — Suggested word count derived from criterion weight × question type.
 *  This is bid-management heuristic, not gospel: a Must under a 40%-weighted
 *  criterion deserves more words than a Could under 10%. The numbers come
 *  from typical UK ITT pacing for a 60-page proposal. */
function suggestedWordCount(
  type: string | undefined,
  criterionLabel: string | undefined
): number | undefined {
  const t = (type || '').toLowerCase();
  let base = 350;
  if (t.includes('must')) base = 500;
  else if (t.includes('should')) base = 350;
  else if (t.includes('could')) base = 200;
  else if (t.includes('pass')) base = 250;
  // Multiplier from criterion weight (extracted from "Foo 40%")
  if (criterionLabel) {
    const m = criterionLabel.match(/(\d+(?:\.\d+)?)\s*%/);
    if (m) {
      const pct = parseFloat(m[1]);
      if (Number.isFinite(pct)) {
        // 10% → 0.7×, 20% → 1.0×, 30% → 1.3×, 40%+ → 1.5×
        const multiplier = Math.max(0.5, Math.min(1.6, pct / 20));
        base = Math.round(base * multiplier);
      }
    }
  }
  // Round to a friendly number
  return Math.round(base / 50) * 50;
}

export function buildQuestionMatrix(args: {
  questions?: Question[] | any[];
  refinedQuestions?: any[];
  draftedQuestions?: DraftedQuestion[] | any[];
  evaluationCriteria?: EvaluationCriteria[];
  selectionMetadata?: Record<string, QuestionSelectionMetadata>;
  excludedIds?: Set<string>;
  pastTenderMatches?: Record<string, PastTenderMatch[]>;
  /** When 'generate', rows whose match in `refinedQuestions` started from an
   *  empty original are reclassified as 'drafted' so the UI labels them as
   *  newly authored instead of "refined". The backend currently lands all AI
   *  output under refined_questions regardless of mode, which made every
   *  generate-mode row show "Refined" even though no refinement happened. */
  responseMode?: 'auto' | 'generate' | 'refine';
}): QuestionMatrixRow[] {
  const {
    questions = [],
    refinedQuestions = [],
    draftedQuestions = [],
    evaluationCriteria,
    selectionMetadata,
    excludedIds,
    pastTenderMatches,
    responseMode,
  } = args;
  const isGenerate = responseMode === 'generate';

  // Index refined and drafted by question_id so we can look them up.
  const refinedById = new Map<string, any>();
  for (const r of refinedQuestions || []) {
    const key =
      r.question_id ||
      r.id ||
      // Some backends emit only `question` (text) — fall back to text-equality lookup later.
      undefined;
    if (key) refinedById.set(String(key), r);
  }
  const draftedById = new Map<string, any>();
  for (const d of draftedQuestions || []) {
    const key = d.question_id || d.id;
    if (key) draftedById.set(String(key), d);
  }

  // Refinement payloads sometimes only key by question text (the scorer
  // returns `question`, not `question_id`). Build a text lookup as fallback.
  const refinedByText = new Map<string, any>();
  for (const r of refinedQuestions || []) {
    const txt = (r.question || r.text || '').trim();
    if (txt) refinedByText.set(txt, r);
  }

  const rows: QuestionMatrixRow[] = [];
  for (const q of questions || []) {
    const qId = String(q.id || q.question_id || '');
    const qText = q.text || q.question || '';
    if (!qId && !qText) continue;
    const criterion_label = matchCriterion(qText, evaluationCriteria);
    const suggested_words = suggestedWordCount(q.type, criterion_label);
    const meta = qId && selectionMetadata ? selectionMetadata[qId] : undefined;

    // P3 — user-excluded wins over everything.
    if (qId && excludedIds && excludedIds.has(qId)) {
      rows.push({
        question_id: qId,
        question: qText,
        section: q.section,
        type: q.type,
        criterion_label,
        status: 'user_excluded',
        improvement_score: null,
        improvement_summary: '',
        current_response: '',
        original_response: q.response || '',
        source: 'extracted_no_response',
        selection_metadata: meta,
        suggested_words,
      });
      continue;
    }

    // 1) Refined match (had inline content; AI improved).
    const refined =
      refinedById.get(qId) ||
      (qText ? refinedByText.get(qText.trim()) : undefined);
    if (refined) {
      // In generate mode the backend dumps newly-authored drafts under
      // refined_questions too. Detect that case so the UI shows "Drafted"
      // instead of "Refined": if the original was empty (no inline bidder
      // response existed), this entry is a fresh draft, not a refinement.
      const originalText = (refined.original_response || q.response || '').trim();
      const isFreshDraft = isGenerate && originalText.length === 0;
      rows.push({
        question_id: qId,
        question: qText,
        section: q.section,
        type: q.type,
        criterion_label,
        status: isFreshDraft ? 'drafted' : 'refined',
        improvement_score:
          typeof refined.improvement_score === 'number'
            ? refined.improvement_score
            : null,
        improvement_summary: refined.improvement_summary || '',
        current_response: refined.refined_response || '',
        original_response: refined.original_response || q.response || '',
        source: isFreshDraft ? 'drafted' : 'refined',
        selection_metadata: meta,
        suggested_words,
      });
      continue;
    }

    // 2) Drafted match (no inline content; AI authored).
    const drafted = draftedById.get(qId);
    if (drafted) {
      rows.push({
        question_id: qId,
        question: qText,
        section: q.section,
        type: q.type,
        criterion_label,
        status: 'drafted',
        improvement_score:
          typeof drafted.improvement_score === 'number'
            ? drafted.improvement_score
            : null,
        improvement_summary: drafted.improvement_summary || '',
        current_response: drafted.drafted_response || '',
        original_response: '',
        source: 'drafted',
        selection_metadata: meta,
        suggested_words,
        kb_retrieval: drafted.kb_retrieval,
        kb_sources: drafted.sources,
      });
      continue;
    }

    // 3) Otherwise — extracted but neither refined nor drafted yet.
    rows.push({
      question_id: qId,
      question: qText,
      section: q.section,
      type: q.type,
      criterion_label,
      status: 'needs_draft',
      improvement_score: null,
      improvement_summary: '',
      current_response: '',
      original_response: q.response || '',
      source: 'extracted_no_response',
      selection_metadata: meta,
      suggested_words,
    });
  }

  // Attach tender-bank similarity matches per question (no-op when the bank
  // is empty — match_past_tenders returns {} in that case).
  if (pastTenderMatches) {
    for (const r of rows) {
      const m = pastTenderMatches[r.question_id];
      if (m && m.length) r.past_matches = m;
    }
  }

  return rows;
}

/** Status sort order so completed AI work surfaces first by default
 *  (refined → drafted → awaiting draft → approved → excluded). */
export const STATUS_SORT_ORDER: Record<string, number> = {
  refined: 0,
  drafted: 1,
  needs_draft: 2,
  approved: 3,
  user_excluded: 4,
};

// ────────────────────────────────────────────────────────────────────────────
// Final Report synthesizer (Phases A + B)
//
// Single source of truth that turns the raw analysis state into the
// structured FinalReport surface consumed by FinalReportSummary,
// FinalReportViewer, and the (Phase C) export/checklist tooling.
//
// This synthesizer is the *fallback* path: when the backend (Phase D) emits a
// fully-structured FinalReportPayload, callers will use that directly. Until
// then the same UI components read from this synthesised shape, so we never
// have two divergent renderers.
// ────────────────────────────────────────────────────────────────────────────

/** Sum the words in a response — used by checklist gating + AI disclosure. */
function _wordCount(text: string | undefined | null): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

/** Pick the average of a list of nullable numbers, ignoring nulls. */
function _avgScore(scores: Array<number | null | undefined>): number | null {
  const valid = scores.filter((s): s is number => typeof s === 'number');
  if (!valid.length) return null;
  return valid.reduce((acc, s) => acc + s, 0) / valid.length;
}

/** Best-effort criterion match — same logic the QuestionMatrix uses. */
function _criterionFor(qText: string, criteria?: EvaluationCriteria[]): string | undefined {
  if (!qText || !criteria || !criteria.length) return undefined;
  const lower = qText.toLowerCase();
  for (const c of criteria) {
    const name = (c.name || '').toLowerCase();
    if (name && lower.includes(name)) return c.name;
  }
  return undefined;
}

export interface BuildFinalReportArgs {
  /** Raw refined responses (from state.refined_questions). */
  refinedQuestions?: any[];
  /** Drafted responses authored for unanswered questions. */
  draftedQuestions?: DraftedQuestion[] | any[];
  /** All extracted questions — used to surface excluded ones. */
  questions?: Question[] | any[];
  /** Evaluation criteria for the criterion roll-up + criteria list. */
  evaluationCriteria?: EvaluationCriteria[];
  /** Compliance items extracted from the source PDF (P6). */
  complianceItems?: ComplianceItem[];
  /** Tender-bank past matches keyed by question_id. */
  pastTenderMatches?: Record<string, PastTenderMatch[]>;
  /** Per-question selection metadata (used to find user_excluded). */
  selectionMetadata?: Record<string, QuestionSelectionMetadata>;
  /** IDs explicitly excluded by the bidder via the matrix UI. */
  excludedIds?: Set<string>;
  /** Pre-computed legacy markdown blob from the backend's generate_final_report. */
  legacyMarkdown?: string;
  /** Optional fallback overall score if no rubric scores are available. */
  fallbackOverallScore?: number | null;
  /** Optional executive justification string from quality_check_result. */
  submissionJustification?: string;
  /** AI model id used during refinement / drafting (PPN disclosure). */
  modelId?: string;
  /** When 'generate', the rubric scorer didn't run (no baseline to compare
   *  against), so the headline score falls back to KB grounding quality
   *  averaged across drafted rows. Prevents the meaningless 0/100 display. */
  responseMode?: 'auto' | 'generate' | 'refine';
}

/** Map a DraftedQuestion's coverage assessment to a sortable tier. */
const _COVERAGE_TIERS = ['excellent', 'good', 'fair', 'poor', 'critical'] as const;
type CoverageTier = (typeof _COVERAGE_TIERS)[number];

function _coverageTier(d: any): CoverageTier | null {
  const a = (d?.kb_retrieval?.coverage?.assessment || '').toLowerCase();
  return (_COVERAGE_TIERS as readonly string[]).includes(a) ? (a as CoverageTier) : null;
}

/** Build the FinalReport payload from the raw analysis state. */
export function buildFinalReport(args: BuildFinalReportArgs): FinalReport {
  const {
    refinedQuestions = [],
    draftedQuestions = [],
    questions = [],
    evaluationCriteria = [],
    complianceItems = [],
    pastTenderMatches = {},
    excludedIds,
    legacyMarkdown = '',
    fallbackOverallScore = null,
    submissionJustification = '',
    modelId = 'unknown',
    responseMode,
  } = args;
  const isGenerateRun = responseMode === 'generate';

  // ── Refined responses ────────────────────────────────────────────────────
  const refined: FinalReportResponse[] = refinedQuestions.map((r: any, idx: number) => {
    const qid = r.question_id || r.id || `refined-${idx}`;
    const qText = r.question || r.text || '';
    const responseText = r.refined_response || r.improved_response || r.response || '';
    const score = typeof r.improvement_score === 'number' ? r.improvement_score : null;
    return {
      question_id: String(qid),
      question: qText,
      response: responseText,
      section_number: idx + 1,
      source: 'refined',
      improvement_score: score,
      improvement_summary: r.improvement_summary || '',
      suggested_words: typeof r.suggested_words === 'number' ? r.suggested_words : undefined,
      word_count: _wordCount(responseText),
      past_tender_matches: pastTenderMatches[String(qid)] || undefined,
    };
  });

  // ── Drafted responses ────────────────────────────────────────────────────
  const drafted: FinalReportResponse[] = (draftedQuestions || []).map((d: any, idx: number) => {
    const qid = d.question_id || d.id || `drafted-${idx}`;
    const qText = d.question || '';
    const responseText = d.drafted_response || '';
    const score = typeof d.improvement_score === 'number' ? d.improvement_score : null;
    return {
      question_id: String(qid),
      question: qText,
      response: responseText,
      section_number: refined.length + idx + 1,
      source: 'drafted',
      improvement_score: score,
      improvement_summary: d.improvement_summary || '',
      word_count: _wordCount(responseText),
      past_tender_matches: pastTenderMatches[String(qid)] || undefined,
      kb_coverage_tier: _coverageTier(d),
    };
  });

  // ── Excluded questions (P3 — bidder opted out) ───────────────────────────
  const excluded: FinalReportResponse[] = [];
  if (excludedIds && excludedIds.size) {
    let ord = 0;
    for (const q of questions || []) {
      const qid = String(q.id || q.question_id || '');
      if (qid && excludedIds.has(qid)) {
        excluded.push({
          question_id: qid,
          question: q.text || q.question || '',
          response: q.response || '',
          section_number: ++ord,
          source: 'excluded',
          word_count: _wordCount(q.response || ''),
        });
      }
    }
  }

  // ── Score: prefer the anchored quality score (0-100) over the heuristic ──
  // The per-question `improvement_score` comes from a HEURISTIC specificity
  // counter (numbers, %s, ISO codes) — averaging it as the headline "Average
  // response score" produces 12/100 even when the actual quality (anchored
  // QC) is 71/100, because most refinements add prose specificity not
  // numeric specificity.
  //
  // After the QC rewrite (response_subgraph quality_check_agent ->
  // INITIAL_REVIEW_PROMPT anchored rubric + recompute), the canonical
  // quality score is the QC score / improvement_analysis.final_score /
  // initial_review.score — all on the same 0-100 anchored scale. Use that
  // as the headline; fall back to the heuristic average only when no
  // anchored score is present (legacy / early-failure runs).
  const allScores = [...refined.map((r) => r.improvement_score), ...drafted.map((r) => r.improvement_score)];
  const rubricAvg = _avgScore(allScores);

  // Generate-mode fallback: the rubric scorer doesn't run on freshly drafted
  // rows (no original to compare against), so the rubric average is null and
  // any cached fallbackOverallScore is often 0 (degenerate). Synthesize a
  // "draft quality" score from KB grounding tiers so the Final Report shows
  // a meaningful headline number instead of 0/100.
  const TIER_TO_SCORE: Record<string, number> = {
    excellent: 95,
    good: 80,
    fair: 60,
    poor: 35,
    critical: 15,
    ungrounded: 10,
  };
  const tierScores: number[] = [];
  for (const d of draftedQuestions || []) {
    const tier = (d?.kb_retrieval?.coverage?.assessment || '').toLowerCase();
    if (tier && TIER_TO_SCORE[tier] !== undefined) {
      tierScores.push(TIER_TO_SCORE[tier]);
    } else if (typeof d?.kb_retrieval?.coverage?.confidence_score === 'number') {
      tierScores.push(Math.max(0, Math.min(1, d.kb_retrieval.coverage.confidence_score)) * 100);
    }
  }
  const syntheticGenerateScore = tierScores.length > 0
    ? Math.round((tierScores.reduce((a, b) => a + b, 0) / tierScores.length) * 10) / 10
    : null;

  // Score selection. The headline `overall_score` reflects PROSE QUALITY in
  // both modes; KB grounding lives in its own field (`kb_grounding_score`)
  // so a sparse KB doesn't depress the headline below what the actual writing
  // earned. Industry pattern (Loopio, Responsive, AutogenAI): one prose-
  // centric trust score + a separate evidence/citation panel.
  //
  // GENERATE: per-question rubric average → synthetic KB tier as last resort
  //   (when no per-question scores at all, which means rendering "—" is worse
  //   than rendering KB grounding as a degraded headline).
  // REFINE: anchored QC > rubric average (existing priority — unchanged).
  const fallbackIsRealNumber = typeof fallbackOverallScore === 'number' && fallbackOverallScore > 0;
  const overall =
    isGenerateRun
      ? rubricAvg !== null
          ? Math.round(rubricAvg * 10) / 10
          : syntheticGenerateScore
      : fallbackIsRealNumber
        ? fallbackOverallScore
        : rubricAvg !== null
          ? Math.round(rubricAvg * 10) / 10
          : null;

  // ── Criterion roll-up ────────────────────────────────────────────────────
  const breakdown: CriterionBreakdown[] = (evaluationCriteria || []).map((c) => {
    const tagged = [...refined, ...drafted].filter((r) => _criterionFor(r.question, [c]) === c.name);
    const avg = _avgScore(tagged.map((t) => t.improvement_score));
    const weight = typeof c.weight === 'number' ? c.weight : 0;
    const weighted = avg !== null && weight ? Math.round((weight * avg) / 100 * 10) / 10 : null;
    return {
      criterion: c.name || '',
      weight,
      answers_in_criterion: tagged.length,
      average_score: avg !== null ? Math.round(avg * 10) / 10 : null,
      weighted_contribution: weighted,
    };
  });

  // ── Evidence summary (Phase B) ───────────────────────────────────────────
  const tier_counts: EvidenceSummary['tier_counts'] = {};
  const flagged_question_ids: string[] = [];
  const recurring_gaps_set = new Map<string, number>();
  let totalDrafted = 0;
  for (const d of draftedQuestions || []) {
    const tier = _coverageTier(d);
    if (!tier) continue;
    totalDrafted += 1;
    tier_counts[tier] = (tier_counts[tier] || 0) + 1;
    if (tier === 'poor' || tier === 'critical') {
      const qid = String(d.question_id || d.id || '');
      if (qid) flagged_question_ids.push(qid);
    }
    const gaps: string[] = d?.kb_retrieval?.coverage?.missing_categories || [];
    for (const g of gaps) {
      recurring_gaps_set.set(g, (recurring_gaps_set.get(g) || 0) + 1);
    }
  }
  // Surface recurring gaps that show up in 2+ drafted questions, sorted by frequency.
  const recurring_gaps = Array.from(recurring_gaps_set.entries())
    .filter(([, n]) => n >= 2)
    .sort((a, b) => b[1] - a[1])
    .map(([g]) => g);

  // Positive grounding signal — what the system DID cite, not just what
  // it failed to cite. Without this the panel reads "1 weak draft" even
  // on runs where 50 KB chunks fed 10 refined responses successfully.
  let kbExcerptsUsed = 0;
  let pastTenderExcerptsUsed = 0;
  let refinedWithKb = 0;
  for (const r of refinedQuestions || []) {
    const kb = typeof r.kb_examples_used === 'number' ? r.kb_examples_used : 0;
    const pt = typeof r.past_tender_chunks_used === 'number' ? r.past_tender_chunks_used : 0;
    kbExcerptsUsed += kb;
    pastTenderExcerptsUsed += pt;
    if (kb > 0 || pt > 0) refinedWithKb += 1;
  }
  let questionsWithPastMatches = 0;
  const titles = new Map<string, number>();
  const buyers = new Map<string, number>();
  for (const matches of Object.values(pastTenderMatches || {})) {
    if (!Array.isArray(matches) || !matches.length) continue;
    questionsWithPastMatches += 1;
    for (const m of matches) {
      const meta: any = (m as any).metadata || m;
      const title: string = String(meta.title || meta.tender_title || '').trim();
      const buyer: string = String(meta.buyer_organisation || meta.buyer || '').trim();
      if (title) titles.set(title, (titles.get(title) || 0) + 1);
      if (buyer) buyers.set(buyer, (buyers.get(buyer) || 0) + 1);
    }
  }
  const topByCount = (m: Map<string, number>, n: number) =>
    Array.from(m.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, n)
      .map(([k]) => k);

  const evidenceSummary: EvidenceSummary = {
    tier_counts,
    flagged_question_ids,
    recurring_gaps,
    total_drafted: totalDrafted,
    positive_signal: {
      refined_with_kb: refinedWithKb,
      kb_excerpts_used: kbExcerptsUsed,
      past_tender_excerpts_used: pastTenderExcerptsUsed,
      questions_with_past_matches: questionsWithPastMatches,
      top_past_tender_titles: topByCount(titles, 5),
      top_buyer_organisations: topByCount(buyers, 5),
    },
  };

  // ── AI disclosure block (Phase B / PPN-035) ──────────────────────────────
  const inlineOriginalsKept = (questions || []).filter((q: any) => {
    const qid = String(q.id || q.question_id || '');
    if (excludedIds && excludedIds.has(qid)) return false;
    if (refined.some((r) => r.question_id === qid)) return false;
    if (drafted.some((r) => r.question_id === qid)) return false;
    return Boolean((q.response || '').trim());
  }).length;

  const aiDisclosure: AIDisclosure = {
    model_id: modelId,
    generated_at: new Date().toISOString(),
    refined_count: refined.length,
    drafted_count: drafted.length,
    excluded_count: excluded.length,
    original_count: inlineOriginalsKept,
    disclosure_text: _composeDisclosureText({
      modelId,
      refined: refined.length,
      drafted: drafted.length,
      excluded: excluded.length,
      original: inlineOriginalsKept,
    }),
  };

  // ── Evaluation criteria projection ───────────────────────────────────────
  const criteriaProjection: EvaluationCriterion[] = (evaluationCriteria || []).map((c: any) => ({
    name: c.name || '',
    weight: typeof c.weight === 'number' ? c.weight : undefined,
    description: c.description || '',
    priority_level: (c.priority_level as EvaluationCriterion['priority_level']) || 'Should',
  }));

  return {
    date: new Date().toISOString().slice(0, 10),
    overall_score: overall,
    kb_grounding_score: syntheticGenerateScore,
    max_score: 100,
    evaluation_criteria: criteriaProjection,
    submission_justification: submissionJustification,
    refined_responses: refined,
    drafted_responses: drafted,
    excluded_questions: excluded,
    compliance_items: complianceItems || [],
    evidence_summary: evidenceSummary,
    ai_disclosure: aiDisclosure,
    criterion_breakdown: breakdown,
    full_report_markdown: legacyMarkdown,
  };
}

function _composeDisclosureText(args: {
  modelId: string;
  refined: number;
  drafted: number;
  excluded: number;
  original: number;
}): string {
  const lines = [
    'AI Use Disclosure (UK Cabinet Office PPN-035 alignment):',
    `• Model: ${args.modelId}`,
    `• ${args.refined} bidder-authored response${args.refined === 1 ? '' : 's'} refined by AI using the knowledge base.`,
    `• ${args.drafted} response${args.drafted === 1 ? '' : 's'} authored from scratch by AI for previously-unanswered questions, grounded in the knowledge base.`,
  ];
  if (args.original) lines.push(`• ${args.original} response${args.original === 1 ? '' : 's'} are the bidder's original wording, unchanged.`);
  if (args.excluded) lines.push(`• ${args.excluded} question${args.excluded === 1 ? '' : 's'} were excluded by the bidder.`);
  lines.push('• All AI outputs were reviewed by the bidder before submission.');
  return lines.join('\n');
}

/** Compact CSV export of the matrix in compliance-matrix shape. */
export function questionMatrixToCsv(rows: QuestionMatrixRow[]): string {
  const escape = (v: any) => {
    const s = v === null || v === undefined ? '' : String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const header = [
    'Question ID',
    'Section',
    'Type',
    'Criterion',
    'Status',
    'Score',
    'Question',
    'Response',
  ];
  const lines = [header.map(escape).join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.question_id,
        r.section || '',
        r.type || '',
        r.criterion_label || '',
        r.status,
        typeof r.improvement_score === 'number' ? r.improvement_score : '',
        r.question,
        r.current_response || '',
      ]
        .map(escape)
        .join(',')
    );
  }
  return lines.join('\n');
}

